import { FC, useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaCheck, FaPen, FaPlus } from 'react-icons/fa';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { EventWrapperProps } from 'react-big-calendar';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';

import { getEventStyle } from 'utils/helpers/leaves';
import { buildClientEntity } from 'utils/helpers/client';

import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { multiSignDocAsEmployer } from 'routes/client/documents/helpers';

import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import RejectLeave from './RejectLeave';

interface Props extends EventWrapperProps {
  event: any;
  continuesAfter?: boolean;
  overtimeCalendar?: boolean;
  onAddOvertime?: (date: Date) => void;
}

const CustomEventWrapper: FC<Props> = ({ event, continuesAfter, overtimeCalendar, onAddOvertime }: Props) => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { showModal } = useModal();
  const queryClient = useQueryClient();
  const { language } = useLanguageContext();
  const isLanguageEN = language === 'en';

  const [index, setIndex] = useState<number | null>(null);
  const [isApproving, setIsAproving] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isApprovingAndSigning, setIsApprovingAndSigning] = useState(false);
  const [classNames, setClassNames] = useState('');

  useEffect(() => {
    const updateClassNames = () => {
      if (window.innerWidth < 1600) {
        setClassNames('d-flex justify-content-center pr-5 pl-2 pt-2');
      } else {
        setClassNames('d-flex justify-content-center pr-3 pt-4');
      }
    };

    // Set the class names initially
    updateClassNames();

    // Listen for window resize events
    window.addEventListener('resize', updateClassNames);

    // Cleanup listener when component is unmounted
    return () => {
      window.removeEventListener('resize', updateClassNames);
    };
  }, []);

  const approveLeave = (leaveId: number) => {
    return API.post(`leaves/${leaveId}/approve`)
      .then(res => res.data)
      .then(() => {
        queryClient.resetQueries(buildClientEntity('leaves', client));
        showModal({
          type: 'success',
          heading: t('common.success'),
          content: t('leaves.successfullyApproved'),
        });
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const signLeave = (leaveId: number) => {
    return API.get(`leaves/${leaveId}/documents-holders`)
      .then(res => res.data)
      .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
        multiSignDocAsEmployer(
          [reqDocHolderId, orderDocHolderId],
          showModal,
          () => queryClient.resetQueries(buildClientEntity('leaves', client)),
          '',
          t
        );
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const approveAndSignLeave = (leaveId: number) => {
    return API.post(`leaves/${leaveId}/approve`)
      .then(res => res.data)
      .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
        multiSignDocAsEmployer(
          [reqDocHolderId, orderDocHolderId],
          showModal,
          () => queryClient.resetQueries(buildClientEntity('leaves', client)),
          '',
          t
        );
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const renderActions = (
    buttons: {
      type: string;
      text: string;
    }[],
    leaveId: number,
    requestDocId: number
  ) => {
    return buttons?.map(button => {
      const { type, text } = button;
      if (type === 'approve')
        return (
          <div
            className="d-inline-block ml-2"
            style={{ cursor: 'pointer' }}
          >
            <TooltipWrapper
              id="tooltip-approve"
              overlay={text}
            >
              <Button
                className="d-inline-block"
                key={leaveId}
                type="button"
                btnType="icon"
                size="sm"
                disabled={isApproving && leaveId === index}
                loading={isApproving && leaveId === index}
                onClick={() => {
                  setIndex(leaveId);
                  setIsAproving(true);
                  approveLeave(leaveId).then(() => {
                    setIndex(null);
                    setIsAproving(false);
                  });
                }}
              >
                <FaCheck
                  color="#32bfa3"
                  size={14}
                />
              </Button>
            </TooltipWrapper>
          </div>
        );
      if (type === 'sign')
        return (
          <div
            className="d-inline-block ml-2"
            style={{ cursor: 'pointer' }}
          >
            <TooltipWrapper
              id="tooltip-approve"
              overlay={text}
            >
              <Button
                className="d-inline-block"
                key={leaveId}
                type="button"
                btnType="icon"
                size="sm"
                disabled={isSigning && leaveId === index}
                loading={isSigning && leaveId === index}
                onClick={() => {
                  setIndex(leaveId);
                  setIsSigning(true);
                  signLeave(leaveId).then(() => {
                    setIndex(null);
                    setIsSigning(false);
                  });
                }}
              >
                <FaPen
                  color="#4492db"
                  size={14}
                />
              </Button>
            </TooltipWrapper>
          </div>
        );
      if (type === 'approve_and_sign')
        return (
          <div
            className="d-inline-block ml-2"
            style={{ cursor: 'pointer' }}
          >
            <TooltipWrapper
              id="tooltip-approve"
              overlay={text}
            >
              <Button
                className="d-inline-block"
                key={leaveId}
                type="button"
                btnType="icon"
                size="sm"
                disabled={isApprovingAndSigning && leaveId === index}
                loading={isApprovingAndSigning && leaveId === index}
                onClick={() => {
                  setIndex(leaveId);
                  setIsApprovingAndSigning(true);
                  approveAndSignLeave(leaveId).then(() => {
                    setIndex(null);
                    setIsApprovingAndSigning(false);
                  });
                }}
              >
                <FaCheck
                  color="#4492db"
                  size={14}
                />
              </Button>
            </TooltipWrapper>
          </div>
        );
      if (type === 'reject')
        return (
          <div
            className="d-inline-block"
            style={{ cursor: 'pointer' }}
          >
            <RejectLeave
              leaveId={leaveId}
              buttonText={text}
              iconOnly
              handlePreview={() => handlePreviewDoc({ showModal, id: requestDocId })}
              invalidate={() => queryClient.invalidateQueries(buildClientEntity('leaves', client))}
            />
          </div>
        );

      return null;
    });
  };

  const handleAdd = (addDate: Date) => {
    if (onAddOvertime) {
      onAddOvertime(addDate);
    }
  };

  const isWeekend = (d: Date) => {
    const dayNow = new Date(d).getDay();
    return dayNow === 0 || dayNow === 6; // 0 is Sunday and 6 is Saturday
  };

  const isEventHoliday = event.type === 'holiday';
  const isEventHolidayOrWeekend = isEventHoliday || isWeekend(event.date);

  const eventOverlay = isEventHoliday ? (
    `${t(`leaves.${event.type}`)} ${' - '} ${isLanguageEN ? event.name_en || '' : event.name}`
  ) : (
    // We need span here, to add `<br />` element, so we can render the comment on a new line
    <span>
      {t(`leaves.${event.type}`)} {' - '} {t(`leaves.${event.status}`)}. <br />
      {event.comment ? `${t('leaves.comment')}: ${event.comment}` : ''}
    </span>
  );

  const eventName = isEventHoliday ? (isLanguageEN && event.name_en) || event.name : event.user_name;
  const isHalfDay = event.leave_days === 1;

  const iconSize = window.innerWidth < 1600 ? 22 : 30;

  if (!continuesAfter && overtimeCalendar) {
    return (
      <div
        className={classNames}
        style={getEventStyle(event)}
      >
        {isEventHolidayOrWeekend && (
          <div
            className="d-inline-block"
            style={{ cursor: 'pointer' }}
          >
            <TooltipWrapper
              id="tooltip-approve"
              overlay={t('structure.overtime.overtimeHeading')}
            >
              <Button
                className="d-inline-block"
                type="button"
                btnType="icon"
                size="sm"
                onClick={() => handleAdd(event.date)}
              >
                <FaPlus
                  color="#32bfa3"
                  size={iconSize}
                />
              </Button>
            </TooltipWrapper>
          </div>
        )}
      </div>
    );
  }

  if (!continuesAfter) {
    return (
      <div
        className="d-flex justify-content-between pr-2 pl-2 w-100"
        style={getEventStyle(event)}
      >
        <TooltipWrapper
          id="tooltip-calendar-event"
          overlay={eventOverlay}
          wrapperClass="d-block d-flex justify-content-between w-auto"
        >
          <EventName className="mb-0">{eventName}</EventName>
        </TooltipWrapper>
        <div style={{ minWidth: '46px', textAlign: 'end' }}>
          {renderActions(event.buttons, event.id, event.request_document_id)}
        </div>
        {isHalfDay && (
          <TooltipWrapper
            id="tooltip-calendar-event"
            overlay={`${t('leaves.requestedHalfDay')} - ${t(`leaves.${event.type}`)}`}
            wrapperClass="pl-1"
          >
            <span style={{ lineHeight: '15px', display: 'block', padding: '4px 0 0' }}>&#xBD;</span>
          </TooltipWrapper>
        )}
      </div>
    );
  }

  return (
    <div
      className="d-flex justify-content-between pr-2 pl-2"
      style={getEventStyle(event)}
    >
      <EventName className="mb-0">
        {/** @ts-ignore */}
        {eventName}
      </EventName>
    </div>
  );
};

export default CustomEventWrapper;

const EventName = styled.p`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
